import { ActionReducerMapBuilder, createAsyncThunk } from '@reduxjs/toolkit';
import widgetCache from './widgetCache';
import { WidgetCollectionState } from './widgetSlice';
import widgetApi from '@src/api/widget/widgetApi';
import { getGlobalIntl } from '@src/assets/i18n/globalIntl';
import { showGlobalNotification } from '@src/features/interface/interfaceSlice';

/**
 * Deletes the given widget in the database.
 * It also removes the widget from the local cache.
*/
export const deleteWidgetAction = createAsyncThunk('/widgets/deleteWidgetAction', async (
	args: { widgetDbId: string, version: number },
	thunkAPI
): Promise<string> => {

	try {
		const t = getGlobalIntl();

		thunkAPI.dispatch(showGlobalNotification({
			code: 'delete_widget',
			message: t('Widget.AsyncIndicator.Progress', { action: t('CommonWords.Deleting') }),
			showProgress: true,
			duration: 30,
			type: 'info',
		}));

		const response = await widgetApi.deleteWidget(args.widgetDbId);
		if (!response.success) { throw new Error(`Invalid server response`); }

		// Store the widget from the local cache
		await widgetCache.deleteWidget(args.widgetDbId/* , args.version */);

		thunkAPI.dispatch(showGlobalNotification({
			code: 'delete_widget',
			message: t('Widget.AsyncIndicator.Success', { action: t('CommonWords.Deleted') }),
			type: 'success',
		}));

		return args.widgetDbId;
	} catch (error) {
		const t = getGlobalIntl();
		console.error('Failed to delete widget:', error);
		thunkAPI.dispatch(showGlobalNotification({
			code: 'delete_widget',
			title: t('Widget.AsyncIndicator.Failure.Title', { action: t('CommonWords.Delete') }),
			message: error instanceof Error ? error.message : 'Failed to delete widget',
			type: 'error',
		}));

		throw error;
	}
});


export const deleteWidgetReducer = ((builder: ActionReducerMapBuilder<WidgetCollectionState>): void => {

	builder.addCase(deleteWidgetAction.fulfilled, (state, action) => {
		const t = getGlobalIntl();
		
		// Note: We can't dispatch from the reducer, so we'll handle success notification in the thunk
		const otherWidgets = state.customWidgets.filter(widget => widget.dbId !== action.payload);

		state.customWidgets = [
			...otherWidgets,
		];
	});
});

